import {
  getCellRendererFunc,
  getQuoteLegQuantity,
  isValidQuotePrice,
  type PricingModeEnum,
  type Quote,
  type QuoteLeg,
  type TreeRow,
} from '@talos/kyoko';
import type { ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import type { QuoteMarketWithBestBidOffer } from '../../../../utils/rfq';

export class CompositeQuoteRow implements TreeRow {
  dataPath: string[];
  quoteMarket: QuoteMarketWithBestBidOffer;

  /** Top-level properties picked from the Quote */
  PricingMode: PricingModeEnum | undefined;
  AmountCurrency: string | undefined;
  Currency: string | undefined;
  OrderQty: string | undefined;

  /** Getters grabbing values from the quoteMarket, exposing on the same api surface as the LegQuoteRow */
  get MarketAccount() {
    return this.quoteMarket.MarketAccount;
  }

  get BidPx() {
    return isValidQuotePrice(this.quoteMarket.BidPx, this.quoteMarket.QuoteStatus, this.PricingMode)
      ? this.quoteMarket.BidPx
      : undefined;
  }

  get OfferPx() {
    return isValidQuotePrice(this.quoteMarket.OfferPx, this.quoteMarket.QuoteStatus, this.PricingMode)
      ? this.quoteMarket.OfferPx
      : undefined;
  }

  get Quantity() {
    return this.OrderQty;
  }

  get BidQty() {
    return this.quoteMarket.BidQty;
  }

  get OfferQty() {
    return this.quoteMarket.OfferQty;
  }

  get QuoteStatus() {
    return this.quoteMarket.QuoteStatus;
  }

  get rowID() {
    return this.MarketAccount;
  }

  groupColumnValueGetter(params: ValueGetterParams<CompositeQuoteRow>) {
    return this.quoteMarket.MarketAccount;
  }

  groupColumnValueFormatter({ value, context }: ValueFormatterParams<CompositeQuoteRow, string>): string {
    if (!value) {
      return '';
    }

    return context.current.marketAccountsByName?.get(value)?.DisplayName ?? value;
  }

  groupColumnFilterValueGetter(params: ValueGetterParams<CompositeQuoteRow>) {
    return this.groupColumnValueGetter(params);
  }

  groupColumnInnerCellRenderer(params: ICellRendererParams<CompositeQuoteRow, string>) {
    const cellRendererFunc = getCellRendererFunc(params.api, 'MarketAccount');

    if (cellRendererFunc) {
      return cellRendererFunc(params);
    }

    return params.value ?? '';
  }

  constructor(quote: Quote, quoteMarket: QuoteMarketWithBestBidOffer) {
    this.dataPath = [quoteMarket.MarketAccount];
    this.quoteMarket = quoteMarket;
    this.PricingMode = quote.PricingMode;
    this.AmountCurrency = quote.AmountCurrency;
    this.Currency = quote.Currency;
    this.OrderQty = quote.OrderQty;
  }
}

export class LegQuoteRow implements TreeRow {
  dataPath: string[];
  quoteLeg: QuoteLeg;
  PricingMode: PricingModeEnum | undefined;
  Side: Quote['Side'];

  /** Getters grabbing values from the quoteLeg, exposing on the same api surface as the LegQuoteRow */
  get MarketAccount() {
    return this.quoteLeg.MarketAccount;
  }

  get BidPx() {
    return isValidQuotePrice(this.quoteLeg.BidPx, this.quoteLeg.QuoteStatus, this.PricingMode)
      ? this.quoteLeg.BidPx
      : undefined;
  }

  get OfferPx() {
    return isValidQuotePrice(this.quoteLeg.OfferPx, this.quoteLeg.QuoteStatus, this.PricingMode)
      ? this.quoteLeg.OfferPx
      : undefined;
  }

  get Symbol() {
    return this.quoteLeg.Symbol;
  }

  get QuoteStatus() {
    return this.quoteLeg.QuoteStatus;
  }

  get Quantity() {
    return getQuoteLegQuantity(this.Side, this.quoteLeg);
  }

  get BidQty() {
    return this.quoteLeg.BidQty;
  }

  get OfferQty() {
    return this.quoteLeg.OfferQty;
  }

  get Currency() {
    return this.quoteLeg.Currency;
  }

  get AmountCurrency() {
    return this.quoteLeg.AmountCurrency;
  }

  get rowID() {
    return this.dataPath.join('-');
  }

  groupColumnValueGetter(params: ValueGetterParams<CompositeQuoteRow>) {
    return this.quoteLeg.MarketAccount;
  }

  groupColumnValueFormatter({ value, context }: ValueFormatterParams<CompositeQuoteRow, string>): string {
    if (!value) {
      return '';
    }

    return context.current.marketAccountsByName?.get(value)?.DisplayName ?? value;
  }

  groupColumnFilterValueGetter(params: ValueGetterParams<CompositeQuoteRow>) {
    return this.groupColumnValueGetter(params);
  }

  groupColumnInnerCellRenderer(params: ICellRendererParams<CompositeQuoteRow, string>) {
    const cellRendererFunc = getCellRendererFunc(params.api, 'MarketAccount');

    if (cellRendererFunc) {
      return cellRendererFunc(params);
    }

    return params.value ?? '';
  }

  constructor(quote: Quote, quoteLeg: QuoteLeg) {
    this.dataPath = [quoteLeg.MarketAccount, quoteLeg.Symbol];
    this.quoteLeg = quoteLeg;
    this.PricingMode = quote.PricingMode;
    this.Side = quote.Side;
  }
}

export type QuoteBreakdownRow = CompositeQuoteRow | LegQuoteRow;
